<template>
  <div class="tab-content-inner">
    <div class="info-alert">
      <CIcon name="cil-info" class="info-icon" />
      <div>
        <strong>Log de Utilização:</strong> acompanhe o consumo de IA por colaborador.
        Visualize tokens gastos, custo estimado em dólar e número de sessões distintas por período.
      </div>
    </div>

    <!-- Filtro de Período -->
    <div class="filter-row">
      <div class="filter-group">
        <label class="filter-label">Período</label>
        <div class="filter-inputs">
          <input
            type="date"
            class="form-control form-control-sm"
            v-model="filtroInicio"
          />
          <span class="filter-separator">até</span>
          <input
            type="date"
            class="form-control form-control-sm"
            v-model="filtroFim"
          />
          <CButton color="primary" size="sm" @click="loadData()" :disabled="loading">
            <fa-icon icon="search" class="mr-1" />
            Filtrar
          </CButton>
        </div>
      </div>
    </div>

    <!-- Cards Resumo -->
    <div class="summary-cards" v-if="totalizadores">
      <div class="summary-card">
        <div class="summary-icon icon-tokens">
          <fa-icon icon="database" />
        </div>
        <div class="summary-info">
          <div class="summary-value">{{ formatNumber(totalizadores.tokens.total) }}</div>
          <div class="summary-label">Tokens Totais</div>
        </div>
      </div>
      <div class="summary-card">
        <div class="summary-icon icon-cost">
          <fa-icon icon="dollar-sign" />
        </div>
        <div class="summary-info">
          <div class="summary-value">{{ totalizadores.custo_formatado }}</div>
          <div class="summary-label">Valor Gasto Estimado</div>
        </div>
      </div>
      <div class="summary-card">
        <div class="summary-icon icon-sessions">
          <fa-icon icon="comments" />
        </div>
        <div class="summary-info">
          <div class="summary-value">{{ formatNumber(totalizadores.sessoes) }}</div>
          <div class="summary-label">Sessões</div>
        </div>
      </div>
      <div class="summary-card">
        <div class="summary-icon icon-users">
          <fa-icon icon="users" />
        </div>
        <div class="summary-info">
          <div class="summary-value">{{ formatNumber(totalizadores.colaboradores) }}</div>
          <div class="summary-label">Colaboradores</div>
        </div>
      </div>
    </div>

    <!-- Tabela -->
    <div class="table-section">
      <div v-if="loading" class="loading-state">
        <div class="spinner-border text-primary" role="status">
          <span class="sr-only">Carregando...</span>
        </div>
      </div>

      <template v-else>
        <div class="table-responsive" v-if="colaboradores.length > 0">
          <table class="table table-hover log-table">
            <thead>
              <tr>
                <th class="th-rank">#</th>
                <th class="th-sortable" @click="sortBy('nome')">
                  Colaborador
                  <fa-icon
                    v-if="ordenarPor === 'nome'"
                    :icon="ordem === 'asc' ? 'sort-alpha-down' : 'sort-alpha-down-alt'"
                    class="sort-icon"
                  />
                </th>
                <th class="th-sortable text-right" @click="sortBy('sessoes')">
                  Sessões
                  <fa-icon
                    v-if="ordenarPor === 'sessoes'"
                    :icon="ordem === 'asc' ? 'sort-amount-up' : 'sort-amount-down'"
                    class="sort-icon"
                  />
                </th>
                <th class="th-sortable text-right" @click="sortBy('tokens_entrada')">
                  Tokens Entrada
                  <fa-icon
                    v-if="ordenarPor === 'tokens_entrada'"
                    :icon="ordem === 'asc' ? 'sort-amount-up' : 'sort-amount-down'"
                    class="sort-icon"
                  />
                </th>
                <th class="th-sortable text-right" @click="sortBy('tokens_saida')">
                  Tokens Saída
                  <fa-icon
                    v-if="ordenarPor === 'tokens_saida'"
                    :icon="ordem === 'asc' ? 'sort-amount-up' : 'sort-amount-down'"
                    class="sort-icon"
                  />
                </th>
                <th class="th-sortable text-right" @click="sortBy('tokens_totais')">
                  Tokens Total
                  <fa-icon
                    v-if="ordenarPor === 'tokens_totais'"
                    :icon="ordem === 'asc' ? 'sort-amount-up' : 'sort-amount-down'"
                    class="sort-icon"
                  />
                </th>
                <th class="th-sortable text-right" @click="sortBy('custo_usd')">
                  Custo (USD)
                  <fa-icon
                    v-if="ordenarPor === 'custo_usd'"
                    :icon="ordem === 'asc' ? 'sort-amount-up' : 'sort-amount-down'"
                    class="sort-icon"
                  />
                </th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="(item, index) in colaboradores" :key="item.colaborador_id">
                <td class="td-rank">
                  <span class="rank-badge" :class="getRankClass(index)">{{ getRankNumber(index) }}</span>
                </td>
                <td>
                  <div class="colaborador-info">
                    <strong>{{ item.nome }}</strong>
                    <span class="colaborador-email">{{ item.email }}</span>
                  </div>
                </td>
                <td class="text-right">{{ formatNumber(item.estatisticas.sessoes) }}</td>
                <td class="text-right">{{ formatNumber(item.estatisticas.tokens.entrada) }}</td>
                <td class="text-right">{{ formatNumber(item.estatisticas.tokens.saida) }}</td>
                <td class="text-right">
                  <strong>{{ formatNumber(item.estatisticas.tokens.total) }}</strong>
                </td>
                <td class="text-right">
                  <span class="custo-badge">{{ item.estatisticas.custo.formatado }}</span>
                </td>
              </tr>
            </tbody>
          </table>
        </div>

        <!-- Empty State -->
        <div v-else class="empty-state">
          <fa-icon icon="chart-bar" class="empty-icon" />
          <p>Nenhum registro de utilização encontrado no período selecionado.</p>
        </div>

        <!-- Paginação -->
        <div class="pagination-row" v-if="paginacao && paginacao.ultima_pagina > 1">
          <CButton
            variant="outline"
            color="secondary"
            size="sm"
            :disabled="paginacao.pagina_atual <= 1"
            @click="loadData(paginacao.pagina_atual - 1)"
          >
            Anterior
          </CButton>
          <span class="pagination-info">
            Página {{ paginacao.pagina_atual }} de {{ paginacao.ultima_pagina }}
          </span>
          <CButton
            variant="outline"
            color="secondary"
            size="sm"
            :disabled="paginacao.pagina_atual >= paginacao.ultima_pagina"
            @click="loadData(paginacao.pagina_atual + 1)"
          >
            Próxima
          </CButton>
        </div>
      </template>
    </div>
  </div>
</template>

<script>
import { get } from "@/helpers/apiRequest";

export default {
  name: "tab-log-utilizacao",
  data() {
    const now = new Date();
    const inicioMes = new Date(now.getFullYear(), now.getMonth(), 1);
    const fimMes = new Date(now.getFullYear(), now.getMonth() + 1, 0);

    return {
      loading: false,
      colaboradores: [],
      totalizadores: null,
      paginacao: null,
      filtroInicio: this.formatDate(inicioMes),
      filtroFim: this.formatDate(fimMes),
      ordenarPor: "tokens_totais",
      ordem: "desc",
    };
  },
  methods: {
    formatDate(date) {
      const y = date.getFullYear();
      const m = String(date.getMonth() + 1).padStart(2, "0");
      const d = String(date.getDate()).padStart(2, "0");
      return `${y}-${m}-${d}`;
    },
    formatNumber(value) {
      if (value == null) return "0";
      return Number(value).toLocaleString("pt-BR");
    },
    sortBy(campo) {
      if (this.ordenarPor === campo) {
        this.ordem = this.ordem === "desc" ? "asc" : "desc";
      } else {
        this.ordenarPor = campo;
        this.ordem = "desc";
      }
      this.loadData(1);
    },
    getRankNumber(index) {
      const offset = this.paginacao
        ? (this.paginacao.pagina_atual - 1) * this.paginacao.por_pagina
        : 0;
      return offset + index + 1;
    },
    getRankClass(index) {
      const rank = this.getRankNumber(index);
      if (rank === 1) return "rank-gold";
      if (rank === 2) return "rank-silver";
      if (rank === 3) return "rank-bronze";
      return "";
    },
    loadData(page = 1) {
      this.loading = true;

      const params = new URLSearchParams({
        inicio: this.filtroInicio,
        fim: this.filtroFim,
        ordenar_por: this.ordenarPor,
        ordem: this.ordem,
        page: page,
        per_page: 15,
      });

      get(`/admin/log-prompt-ia/colaboradores?${params.toString()}`)
        .then((json) => {
          this.colaboradores = json.data || [];
          if (json.meta) {
            this.totalizadores = json.meta.totalizadores || null;
            this.paginacao = json.meta.paginacao || null;
          }
          this.loading = false;
        })
        .catch(() => {
          this.colaboradores = [];
          this.totalizadores = null;
          this.loading = false;
        });
    },
  },
  mounted() {
    this.loadData();
  },
};
</script>

<style scoped>
.tab-content-inner {
  padding: 20px;
}

.info-alert {
  display: flex;
  gap: 10px;
  padding: 12px 16px;
  background: #edf8f9;
  border: 1px solid #c5e6e9;
  border-radius: 8px;
  margin-bottom: 20px;
  font-size: 13px;
  color: #344054;
  line-height: 1.5;
}

.info-icon {
  flex-shrink: 0;
  margin-top: 2px;
  color: #2298a3;
}

/* ===== FILTRO ===== */
.filter-row {
  margin-bottom: 20px;
}

.filter-label {
  font-size: 11px;
  font-weight: 700;
  color: #667085;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  margin-bottom: 6px;
  display: block;
}

.filter-inputs {
  display: flex;
  align-items: center;
  gap: 8px;
  flex-wrap: wrap;
}

.filter-inputs input[type="date"] {
  max-width: 170px;
  height: 33px;
}

.filter-inputs .btn {
  height: 33px;
  display: inline-flex;
  align-items: center;
}

.filter-separator {
  font-size: 13px;
  color: #667085;
}

/* ===== CARDS RESUMO ===== */
.summary-cards {
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 14px;
  margin-bottom: 24px;
}

.summary-card {
  display: flex;
  align-items: center;
  gap: 14px;
  padding: 16px 18px;
  background: #fff;
  border: 1px solid #e4e7eb;
  border-radius: 10px;
  transition: border-color 0.15s;
}

.summary-card:hover {
  border-color: #b0bec5;
}

.summary-icon {
  width: 42px;
  height: 42px;
  border-radius: 10px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 16px;
  flex-shrink: 0;
}

.icon-tokens {
  background: #f2f2f8;
  color: #3a3e81;
}

.icon-cost {
  background: #e8f5e9;
  color: #2e7d32;
}

.icon-sessions {
  background: #edf8f9;
  color: #2298a3;
}

.icon-users {
  background: #fff3e0;
  color: #e65100;
}

.summary-info {
  min-width: 0;
}

.summary-value {
  font-size: 20px;
  font-weight: 700;
  color: #1d2939;
  line-height: 1.2;
}

.summary-label {
  font-size: 11px;
  font-weight: 600;
  color: #667085;
  text-transform: uppercase;
  letter-spacing: 0.3px;
  margin-top: 2px;
}

/* ===== TABELA ===== */
.table-section {
  background: #fff;
  border: 1px solid #e4e7eb;
  border-radius: 10px;
  overflow: hidden;
}

.loading-state {
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 60px 20px;
}

.log-table {
  margin-bottom: 0;
}

.log-table thead th {
  background: #f9fafb;
  border-bottom: 2px solid #e4e7eb;
  border-top: none;
  font-size: 11px;
  font-weight: 700;
  color: #667085;
  text-transform: uppercase;
  letter-spacing: 0.3px;
  padding: 10px 14px;
  white-space: nowrap;
}

.th-sortable {
  cursor: pointer;
  user-select: none;
  transition: color 0.15s;
}

.th-sortable:hover {
  color: #3a3e81;
}

.sort-icon {
  margin-left: 4px;
  font-size: 10px;
  color: #3a3e81;
}

.th-rank {
  width: 48px;
  text-align: center;
}

.log-table tbody td {
  padding: 12px 14px;
  vertical-align: middle;
  font-size: 13px;
  color: #344054;
  border-bottom: 1px solid #f2f4f7;
}

.log-table tbody tr:hover {
  background: #f9fafb;
}

.td-rank {
  text-align: center;
}

.rank-badge {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 28px;
  height: 28px;
  border-radius: 50%;
  font-size: 12px;
  font-weight: 700;
  background: #f2f4f7;
  color: #667085;
}

.rank-gold {
  background: #fff8e1;
  color: #f9a825;
}

.rank-silver {
  background: #f5f5f5;
  color: #757575;
}

.rank-bronze {
  background: #fbe9e7;
  color: #bf360c;
}

.colaborador-info {
  display: flex;
  flex-direction: column;
}

.colaborador-info strong {
  font-size: 13px;
  color: #1d2939;
}

.colaborador-email {
  font-size: 11px;
  color: #667085;
  margin-top: 1px;
}

.custo-badge {
  display: inline-block;
  padding: 3px 10px;
  border-radius: 12px;
  font-size: 12px;
  font-weight: 600;
  background: #e8f5e9;
  color: #2e7d32;
}

/* ===== EMPTY STATE ===== */
.empty-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 60px 20px;
  color: #667085;
}

.empty-icon {
  font-size: 40px;
  margin-bottom: 12px;
  opacity: 0.4;
}

.empty-state p {
  font-size: 14px;
  margin: 0;
}

/* ===== PAGINAÇÃO ===== */
.pagination-row {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 12px;
  padding: 14px;
  border-top: 1px solid #f2f4f7;
}

.pagination-info {
  font-size: 13px;
  color: #667085;
}

/* ===== RESPONSIVE ===== */
@media (max-width: 992px) {
  .summary-cards {
    grid-template-columns: repeat(2, 1fr);
  }
}

@media (max-width: 576px) {
  .summary-cards {
    grid-template-columns: 1fr;
  }

  .filter-inputs {
    flex-direction: column;
    align-items: stretch;
  }

  .filter-inputs input[type="date"] {
    max-width: 100%;
  }

  .filter-separator {
    text-align: center;
  }
}
</style>
